import React from "react";
import PropTypes from "prop-types";
import { defaultTemplate } from "core/hoc";
import { PreviewLayout } from "core/layouts";
import { BaasicModal, BaasicButton, ApplicationEmptyState, GlobalSignalRListener, BaasicConfirmModal, GlobalItemChangeListener } from "core/components";
import { NikoFacilityOrderStatusBar } from "application/niko-facility-order/components";
import { NikoFacilityOrderUSIntakeFindAndReplaceModal } from "application/niko-facility-order-us-intake/components";
import { NikoFacilityOrderNewFlowExtraSuppliesAddModal } from "application/niko-facility-order-inventory/components";
import { FacilityOrderNewFlowRoundingItemsModal, PrimaryRecipientErrorModal } from "application/facility-order-new-flow-order-summary/components";
import { toDate } from "core/utils";
import { observer } from "mobx-react";
import { Button, DropDownButton, DropDownButtonItem } from "@progress/kendo-react-buttons";
import { moreVerticalIcon } from "@progress/kendo-svg-icons";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { GlobalBeforeUnloadListener, ScrollToTopButton, HoverDropDownButton } from 'core/components';
import { handleKeyDown, handlePaste, getTrimmedPasteResult } from 'core/utils';

const NikoFacilityOrderOrderSummaryPreviewTemplate = function ({ nikoFacilityOrderOrderSummaryPreviewViewStore, t }) {
    const {
        rootStore,
        facilityOrder,
        loaderStore,
        facilityOrderTemplateItemsGroupedByItemCategory,
        showFacilityOrderTemplateItemOrderSummary,
        showFacilityOrderTemplateItemOrderSummaryDetails,
        showDNSItems,
        fetchingData,
        signalRAction,
        localPatientStore,
        onPatientChange,
        itemStore,
        cutbackSalesOrderQuantity,
        cutbackConfirmModal,
        cutbackSOQuantities,
        cutbackSendSeparatelyQuantities,
        savingResource,
        patientName,
        showItem,
        itemName,
        orderHistory,
        goToFacilityPreview,
        goToFacilityCalendar,
        refreshEditModeItems,
        rebuildTooltip,
        sendOrderConfirmModal,
        facilityOrderNewFlowUSIntakeFindAndReplaceModal,
        openFacilityOrderNewFlowUSIntakeFindAndReplaceModal,
        facilityOrderNewFlowExtraSuppliesAddModal,
        openFacilityOrderNewFlowExtraSuppliesAddModal,
        showAllDNS,
        anyDNS,
        showAllDNSItems,
        showNonScheduledItems,
        item,
        setItemValue,
        showFacilityStock,
        sendOrderAll,
        isSendOrderChecking,
        isAdmin,
        disabledSendOrder,
        primaryRecipientErrorModal,
        shouldShowItemActionButtons,
        disabledItemButtons,
        showSendOrderConfirmModal,
        openInsuranceCoverageStatusOverviewEmail,
        clinicalReviewStatus,
        isOrderSummaryStarted,
        expandAllItemDetails,
        collapseAllItemDetails,
        isDns,
        isNonScheduled,
        isNewPatientById,
        hasBackorderedItem,
        editBatchSendSeparatelyQty,
        setSsQtyInputRef,
        getSsQtyInputValue,
        resetSSQuantitySum,
        showRoundOption,
        facilityOrderNewFlowRoundingItemsModal,
        openRoundItemsModal,
        isEditDisabled,
        addTubeConfirmModal,
        addIvPolesConfirmModal,
        handleAdditionalActions,
        handleAdminActions,
        getFieldHistory,
        getItemAddedByHistory,
        downloadReport,
        sendingAgoraPo,
        creatingNikoSOsAllFacilityOrders,
        creatingNikoInvoicesAllFacilityOrders,
        updateBackorderNote,
        goToPatientTemplates,
        isPurchaser,
        isFacilityOrderSent,
        isMasterEditor
    } = nikoFacilityOrderOrderSummaryPreviewViewStore;

    const onChange = (event) => {
        var value = event;
        if (value && value.itemName && value.itemId === undefined) {
            value = {
                itemName: event.itemName,
                itemId: new Date().getTime(),
                description: event.itemName
            };
        }

        setItemValue(value)
    };

    const TooltipContent = observer(({ initialValue, history, fieldName }) => {
        if (!history || history.fieldName !== fieldName) return null;

        const date = new Date(history.eventDate + "Z");
        const formattedDate = date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(',', '');

        if (fieldName === 'ImportAction') {
            return (<div>
                <div>Added by {history.userName}</div>
                <div style={{ fontSize: '11px', opacity: 0.75 }}>{formattedDate}</div>
            </div>);
        }

        return (<div>
            <div>Previous: {initialValue ? initialValue : 'blank'}</div>
            <div style={{ fontSize: '11px', opacity: 0.75 }}>
                {history ? history.userName + ', ' + formattedDate : '—'}
            </div>
        </div>)
    });

    return (
        <PreviewLayout className="container container--new-flow" store={nikoFacilityOrderOrderSummaryPreviewViewStore} emptyRenderer={<ApplicationEmptyState />} layoutFooterVisible={false} loading={loaderStore.loading || fetchingData}>
            {facilityOrder && facilityOrder.facility && <NikoFacilityOrderStatusBar title={`${facilityOrder.facility.name} - DOS ${toDate(facilityOrder.dateOfService)}`} pageViewStore={nikoFacilityOrderOrderSummaryPreviewViewStore} >
                <div className="content__header__actions card--tertiary__data__item">
                    <ReactTooltip positionStrategy="fixed" id="historyTooltip" place="bottom" />
                    <Button className="btn btn--base btn--light u-mar--right--tny" icon="u-icon u-icon--med-lrg u-icon--us-history" onClick={() => orderHistory()} data-tooltip-content="Facility Order History" data-tooltip-id="historyTooltip" />

                    <ReactTooltip positionStrategy="fixed" id="facilityPreviewTooltip" place="bottom" />
                    <a className="btn btn--base btn--light u-mar--right--tny" target='_blank' data-tooltip-content="Facility Preview" data-tooltip-id="facilityPreviewTooltip" onClick={() => goToFacilityPreview()}><i className="u-icon u-icon--sml u-icon--preview--dark"></i></a>

                    <ReactTooltip positionStrategy="fixed" id="orderingTooltip" place="bottom" />
                    <a className="btn btn--base btn--light u-mar--right--tny" target='_blank' data-tooltip-content="Ordering Calendar" data-tooltip-id="orderingTooltip" onClick={() => goToFacilityCalendar()}><i className="u-icon u-icon--sml u-icon--schedules"></i></a>

                </div>
            </NikoFacilityOrderStatusBar>}

            <GlobalSignalRListener groupId={facilityOrder && facilityOrder.id} rootStore={rootStore} action={signalRAction} />
            <GlobalItemChangeListener isChanged={rebuildTooltip} />

            {hasBackorderedItem &&
                <div className="u-flex--space-between u-mar--top--med">
                    <div className="card card--outlined--featured card--outlined--note card--sml card--flex">
                        <div>
                            <i className="u-icon u-icon--sml u-icon--warning-triangle u-mar--right--sml"></i>
                            There are backordered items included in the order.
                        </div>
                    </div>
                </div>
            }

            <div className="filter--top">
                <SearchInput
                    source={localPatientStore.items}
                    textField='name'
                    placeholder='Search Patient'
                    onChange={(value) => { onPatientChange(value) }}
                    value={patientName}
                    disabled={false}
                    filterBy={[{ field: 'name', operator: "contains" }]}
                />
                <ComboBoxWrapper
                    data={itemStore}
                    textField="itemName"
                    dataItemKey="itemId"
                    allowCustom={true}
                    value={item}
                    onChange={(value) => onChange(value)}
                />
            </div>
            <div className="filter--actions">
                <div className="filter--actions__inner">
                    <BaasicButton
                        className={`btn btn--base btn--filter ${!anyDNS ? ' k-button k-disabled' : ''}`}
                        icon="u-icon u-icon--med u-icon--filter-slash"
                        onClick={() => showAllDNSItems()}
                        label={showAllDNS && anyDNS ? `Hide DNS` : `View DNS`}
                        showTooltip={false}
                        disabled={!anyDNS}>
                    </BaasicButton>
                    <BaasicButton
                        className={`btn btn--base btn--filter btn--filter--no-focus ${patientName ? 'k-button k-disabled' : ''} `}
                        icon="u-icon u-icon--med u-icon--chevrons-down--primary"
                        label="Expand All"
                        showTooltip={false}
                        disabled={patientName}
                        onClick={expandAllItemDetails}
                    />
                    <BaasicButton
                        className={`btn btn--base btn--filter btn--filter--no-focus ${patientName ? 'k-button k-disabled' : ''} `}
                        icon="u-icon u-icon--med u-icon--chevrons-up--primary"
                        label="Collapse All"
                        showTooltip={false}
                        disabled={patientName}
                        onClick={collapseAllItemDetails}
                    />
                    {(isFacilityOrderSent ? isMasterEditor : (!isOrderSummaryStarted || isAdmin)) &&
                        <BaasicButton
                            className={`btn btn--base btn--filter ${disabledSendOrder || !showFacilityStock ? ' k-button k-disabled' : ''}`}
                            disabled={disabledSendOrder || !showFacilityStock}
                            label={t('FACILITY_INVENTORY.FACILITY_SUPPLY.STOCK')}
                            showTooltip={false}
                            icon="u-icon u-icon--med u-icon--filter-boxes"
                            onClick={() => openFacilityOrderNewFlowExtraSuppliesAddModal()}>
                        </BaasicButton>
                    }
                    {(isFacilityOrderSent ? isMasterEditor : (!isOrderSummaryStarted || isAdmin)) &&
                        <BaasicButton
                            className={`btn btn--base btn--filter ${disabledSendOrder ? ' k-button k-disabled' : ''}`}
                            disabled={disabledSendOrder}
                            label={'Find and Replace'}
                            showTooltip={false}
                            onClick={() => openFacilityOrderNewFlowUSIntakeFindAndReplaceModal()}
                            icon="u-icon u-icon--med u-icon--filter-search">
                        </BaasicButton>
                    }
                    {(isFacilityOrderSent ? isMasterEditor : (!isOrderSummaryStarted || isAdmin)) &&
                        <BaasicButton
                            className={`btn btn--base btn--filter ${savingResource || disabledSendOrder || !(showRoundOption && facilityOrderTemplateItemsGroupedByItemCategory.some(x => x.items.some(y => y.showRoundOption))) ? 'k-button k-disabled' : ''}`}
                            icon="u-icon u-icon--med u-icon--filter-refresh"
                            label={'Round Cases'}
                            showTooltip={false}
                            disabled={savingResource || disabledSendOrder || !(showRoundOption && facilityOrderTemplateItemsGroupedByItemCategory.some(x => x.items.some(y => y.showRoundOption)))}
                            onClick={() => openRoundItemsModal()}>
                        </BaasicButton>
                    }
                    <BaasicButton
                        className={`btn btn--base btn--filter ${!(clinicalReviewStatus && clinicalReviewStatus.completedOn) ? ' k-button k-disabled' : ''}`}
                        icon="u-icon u-icon--med u-icon--mail-send"
                        label={'Send Overview'}
                        showTooltip={false}
                        disabled={!(clinicalReviewStatus && clinicalReviewStatus.completedOn)}
                        onClick={() => openInsuranceCoverageStatusOverviewEmail()}
                    >
                    </BaasicButton>
                    <HoverDropDownButton
                        text="Download"
                        buttonClass="btn btn--base btn--filter is-download"
                        popupSettings={{
                            animate: false,
                            anchorAlign: { horizontal: 'left', vertical: 'bottom' },
                            popupAlign: { horizontal: 'left', vertical: 'top' },
                            popupClass: 'is-clean'
                        }}
                        onItemClick={(e) => downloadReport(e.item.text)}
                    >
                        <DropDownButtonItem text="Pick Ticket" />
                        <DropDownButtonItem text="Packing List" />
                        <DropDownButtonItem text="Delivery Ticket" />
                        {facilityOrder && facilityOrder.facility && facilityOrder.facility.sendAgoraPo &&
                            <DropDownButtonItem text="Agora PO" />
                        }
                    </HoverDropDownButton>
                    {(isFacilityOrderSent ? isMasterEditor : isAdmin) &&
                        <DropDownButton
                            fillMode="solid"
                            themeColor="primary"
                            text="Admin Actions"
                            onItemClick={(e) => handleAdminActions(e.item.text)}
                            buttonClass="btn btn--base btn--filter is-admin-task"
                            iconCLass="u-icon u-icon--med u-icon--admin-task"
                            disabled={sendOrderAll}
                            popupSettings={{
                                animate: false,
                                anchorAlign: { horizontal: "left", vertical: "bottom" },
                                popupAlign: { horizontal: "left", vertical: "top" },
                                popupClass: 'is-clean is-match-button'
                            }}
                        >
                            <DropDownButtonItem text="Send Packing List (this FO)" />
                            <DropDownButtonItem text="Send Packing List (all FOs)" />
                            <DropDownButtonItem text="Create Niko SOs (all FOs)" disabled={creatingNikoSOsAllFacilityOrders} />
                            <DropDownButtonItem text="Create Niko Invoices (all FOs)" disabled={creatingNikoInvoicesAllFacilityOrders} />
                            {!sendingAgoraPo && facilityOrder && facilityOrder.facility && facilityOrder.facility.sendAgoraPo &&
                                <DropDownButtonItem text="Send Agora PO" />
                            }
                        </DropDownButton>
                    }
                    {facilityOrder && !facilityOrder.sendOrder && facilityOrder.facility && (!facilityOrder.facility.doNotSendIVPoles || facilityOrder.facility.isMonthlyTrachTube) &&
                        <DropDownButton
                            svgIcon={moreVerticalIcon}
                            onItemClick={(e) => handleAdditionalActions(e.item.text)}
                            buttonClass="btn btn--base btn--filter is-icon-only"
                            disabled={sendOrderAll}
                            popupSettings={{
                                animate: false,
                                anchorAlign: { horizontal: "left", vertical: "bottom" },
                                popupAlign: { horizontal: "left", vertical: "top" },
                                popupClass: 'is-clean is-match-button'
                            }}
                        >
                            {!facilityOrder.facility.doNotSendIVPoles &&
                                <DropDownButtonItem text="Add IV Poles" />
                            }
                            {facilityOrder.facility.isMonthlyTrachTube &&
                                <DropDownButtonItem text="Check for Monthly Tubes Now" disabled={facilityOrder.isNotCurrentTubeAdded || facilityOrder.isEnteralWeek} />
                            }
                        </DropDownButton>
                    }
                    {facilityOrder && facilityOrder.sendOrder &&
                        <BaasicButton
                            className="btn btn--base btn--filter"
                            icon="u-icon u-icon--med u-icon--expand--primary"
                            label={'View Pt Sales Orders'}
                            showTooltip={false}
                            onClick={() => goToPatientTemplates()}
                        />
                    }
                </div>
                <div className="filter--actions__inner">
                    {
                        facilityOrder && !facilityOrder.sendOrder &&
                        <BaasicButton
                            className="btn btn--base btn--primary"
                            label={'Send Order'}
                            onClick={() => showSendOrderConfirmModal()}
                            disabled={(facilityOrder && facilityOrder.sendOrder) || sendOrderAll || isSendOrderChecking}
                            rotate icon={
                                (sendOrderAll || isSendOrderChecking)
                                    ? "u-icon u-icon--xmed u-icon--sync u-rotate--login"
                                    : 'u-display--none'
                            }
                        >
                        </BaasicButton>
                    }
                </div>
            </div>
            <div className="u-separator--primary  u-mar--top--lrg"></div>
            <div className="u-mar--top--lrg">
                {<React.Fragment>
                    {facilityOrderTemplateItemsGroupedByItemCategory && facilityOrderTemplateItemsGroupedByItemCategory.length > 0 ?
                        facilityOrderTemplateItemsGroupedByItemCategory.map((group, index) => {
                            return ((!showAllDNS && facilityOrderTemplateItemsGroupedByItemCategory[index].items.length && facilityOrderTemplateItemsGroupedByItemCategory[index].items.some(x => x.facilityOrderTemplateItemOrderSummaryDetails.some(y => !isDns(y))) ||
                                showAllDNS && facilityOrderTemplateItemsGroupedByItemCategory[index].items.length) &&
                                <React.Fragment key={index}>
                                    <div key={group.id} className={` ${group.show ? '' : 'has-border'} u-flex--primary card--import__wrapper`}>
                                        <h3 className="type--wgt--medium">
                                            {group.category}
                                        </h3>
                                        <div onClick={() => showFacilityOrderTemplateItemOrderSummary(index)} className='content__header__btn'><i className={` ${group.show ? '' : 'u-rotate--180'} u-mar--left--xsml u-icon u-icon--med u-icon--arrow-up--primary`}></i></div>
                                    </div>
                                    <div>
                                        {group.show && facilityOrderTemplateItemsGroupedByItemCategory[index].items.map((group2, index2) => {
                                            if (!showAllDNS && !showItem(index, index2) && !patientName && !itemName) {
                                                return;
                                            }
                                            const hasVisibleDetails =
                                                group2.show &&
                                                group2.facilityOrderTemplateItemOrderSummaryDetails.some(detail =>
                                                    (showAllDNS || group2.showDNSItems || !isDns(detail)) &&
                                                    (showAllDNS || group2.showNonScheduledItems || !isNonScheduled(detail))
                                                );

                                            return (
                                                <div
                                                    key={index2}
                                                    className={[
                                                        'card--import',
                                                        'is-column',
                                                        group2.isRefreshed && 'is-warning',
                                                        hasVisibleDetails && 'is-focused',
                                                        !hasVisibleDetails && patientName && 'no-border',
                                                    ].filter(Boolean).join(' ')}
                                                    style={savingResource ? { opacity: 0.5 } : undefined}
                                                >
                                                    <div className="card--import__labels is-top">
                                                        {!patientName &&
                                                            <div className="card--import__labels is-clickable u-cursor--pointer" onClick={() => showFacilityOrderTemplateItemOrderSummaryDetails(index, index2)}>
                                                                <div className={`is-row ${facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].show ? "type--wgt--bold" : ""}`}>
                                                                    {group2.itemName} <span className="card--import__labels__icons u-mar--left--auto"> {!group2.billableToMedicare && <i className="u-icon u-icon--sml u-icon--non-billable u-mar--left--tny"></i>} {(group2.backordered != null ? group2.backordered : group2.itemBackordered) && <i className="u-icon u-icon--med-lrg u-icon--backordered"></i>}</span>
                                                                </div>
                                                                <OverflowRow text={group2.itemId} className={'is-ellipsys'} />
                                                                <div className="is-row is-middle">
                                                                    {group2.orderQuantitySum + " " + group2.unitOfMeasuresAbrv}
                                                                    {(group2.originalSendSeparatelyQuantitySum && group2.originalSendSeparatelyQuantitySum > 0) ? <React.Fragment><ReactTooltip positionStrategy="fixed" id={"ssQtyTip_" + index + "_" + index2} place="top" /><i className="u-icon u-icon--ss u-icon--sml table__icon--ss u-mar--left--lrg" data-tooltip-id={"ssQtyTip_" + index + "_" + index2} data-tooltip-content={group2.sendSeparatelyQuantitySum} /></React.Fragment> : null}
                                                                </div>
                                                                <div className="u-flex--primary u-flex--gap--2 is-row is-box">
                                                                    {group.category == 'Enteral' && group2.orderQuantityUnitOfMeasures && group2.orderQuantityUnitOfMeasures != "" && <i className="u-icon u-icon--intermed u-icon--box"></i>}
                                                                    {group.category == 'Enteral' && group2.orderQuantityUnitOfMeasures}
                                                                </div>
                                                                <div className={`u-flex--primary u-flex--gap--2 is-row is-user ${facilityOrder.sendOrder ? '' : 'is-not-send'}`}>
                                                                    <i className="u-icon u-icon--intermed u-icon--user"></i>
                                                                    {group2.patientCount}
                                                                </div>
                                                                {facilityOrder.sendOrder && !group2.show &&
                                                                    <div className="u-flex--default u-flex--gap--2 is-row">
                                                                        <BackorderNoteControl
                                                                            group={group2}
                                                                            updateBackorderNote={updateBackorderNote}
                                                                            isPurchaser={isPurchaser}
                                                                        />
                                                                    </div>
                                                                }
                                                            </div>
                                                        }

                                                        {patientName &&
                                                            <div className="is-wrap-inside">
                                                                {facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2]
                                                                    .facilityOrderTemplateItemOrderSummaryDetails
                                                                    .filter(detail =>
                                                                        (showAllDNS
                                                                            || facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showDNSItems
                                                                            || !isDns(detail))
                                                                        &&
                                                                        (showAllDNS
                                                                            || facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showNonScheduledItems
                                                                            || !isNonScheduled(detail))
                                                                    )
                                                                    .map((detail, detailIndex) => (
                                                                        <div
                                                                            style={isDns(detail) ? { backgroundColor: '#FCFCFC', color: '#6B6F75', borderRadius: '12px' } : {}}
                                                                            className={`card--import__labels is-filtered ${detail.isNewTemplateItem ? 'new-item' : ''}`}
                                                                            key={"patientItem" + detailIndex}
                                                                        >
                                                                            <div className="is-row type--wgt--bold u-flex--space-between u-padd--right--sml" style={{ position: 'relative' }}>
                                                                                {group2.itemName}
                                                                                <span className="card--import__labels__icons u-mar--left--auto"> {!group2.billableToMedicare && <i className="u-icon u-icon--sml u-icon--non-billable"></i>} {(group2.backordered != null ? group2.backordered : group2.itemBackordered) && <i className="u-icon u-icon--med-lrg u-icon--backordered"></i>}
                                                                                    {detail.isNewTemplateItem && <React.Fragment>
                                                                                        <ReactTooltip positionStrategy="fixed" id={"itemAddedBy" + detail.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={null} history={detail.history} fieldName={"ImportAction"} />} />
                                                                                        <div className="table-col-input__notif table-col-input__notif--insert" data-tooltip-id={"itemAddedBy" + detail.facilityOrderTemplateItemId} onMouseEnter={async () => await getItemAddedByHistory(detail)} data-tooltip-content="" />
                                                                                    </React.Fragment>}
                                                                                </span>
                                                                            </div>
                                                                            <OverflowRow text={group2.itemId} className={'is-ellipsys is-201'} />
                                                                            <div className="is-row">
                                                                                <ReactTooltip positionStrategy="fixed" id={"templateQuantityChanged" + detail.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={detail.initialTemplateQuantity} history={detail.history} fieldName={"Quantity"} />} />
                                                                                {(!detail.isNewTemplateItem && !!detail.initialTemplateQuantity && detail.templateQuantity != detail.initialTemplateQuantity) && <div className="table-col-input__notif opacity-50" data-tooltip-id={"templateQuantityChanged" + detail.facilityOrderTemplateItemId} onMouseEnter={async () => await getFieldHistory(detail, 'Quantity', detail.templateQuantity)} data-tooltip-content="" ></div>}
                                                                                {detail.templateQuantity}
                                                                            </div>
                                                                            <div className="is-row">
                                                                                <ReactTooltip positionStrategy="fixed" id={"orderQuantityChanged" + detail.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={detail.initialOrderQuantity} history={detail.history} fieldName={"SalesOrderQuantity"} />} />
                                                                                {(!detail.isNewTemplateItem && !!detail.initialOrderQuantity && detail.orderQuantity != detail.initialOrderQuantity) && <div className="table-col-input__notif opacity-50" data-tooltip-id={"orderQuantityChanged" + detail.facilityOrderTemplateItemId} onMouseEnter={async () => await getFieldHistory(detail, 'SalesOrderQuantity', detail.orderQuantity)} data-tooltip-content="" ></div>}
                                                                                {detail.orderQuantity}
                                                                                {(detail.isEligibleToSend !== null && detail.activeStatus === "Active" && !isDns(detail) && detail.orderQuantity > 0) &&
                                                                                    (!detail.isEligibleToSend ?
                                                                                        <i className="u-icon u-icon--med u-icon--calendar-x--dark u-align--v--text-bottom u-mar--left--tny" ></i> :
                                                                                        <i className="u-icon u-icon--med u u-icon--calendar-yes--dark u-align--v--text-bottom u-mar--left--tny" ></i>)
                                                                                }
                                                                                {isDns(detail) && <i className=" u-mar--left--med u-icon u-icon--med u-icon--order-cancel--grey" ></i>}
                                                                            </div>
                                                                            <div className="is-row">
                                                                                <ReactTooltip positionStrategy="fixed" id={"eligibilityStatusChanged" + detail.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={detail.initialEligibilityStatus ? detail.initialEligibilityStatus : 'Blank'} history={detail.history} fieldName={"EligibilityStatusId"} />} />
                                                                                {(!isNewPatientById(detail.patientId) && detail.eligibilityStatus != detail.initialEligibilityStatus) && <div className="table-col-input__notif opacity-50" data-tooltip-id={"eligibilityStatusChanged" + detail.facilityOrderTemplateItemId} onMouseEnter={async () => await getFieldHistory(detail, 'EligibilityStatusId', detail.eligibilityStatusId, detail.facilityOrderTemplateId)} data-tooltip-content="" ></div>}
                                                                                {detail.eligibilityStatus}
                                                                            </div>
                                                                            <div className="is-row">
                                                                                <ReactTooltip positionStrategy="fixed" id={"clinicalStatusChanged" + detail.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={detail.initialClinicalStatus ? detail.initialClinicalStatus : 'Blank'} history={detail.history} fieldName={"ClinicalStatusId"} />} />
                                                                                {(!detail.isNewTemplate && detail.clinicalStatus != detail.initialClinicalStatus) && <div className="table-col-input__notif opacity-50" data-tooltip-id={"clinicalStatusChanged" + detail.facilityOrderTemplateItemId} onMouseEnter={async () => await getFieldHistory(detail, 'ClinicalStatusId', detail.clinicalStatusId, detail.facilityOrderTemplateId)} data-tooltip-content="" ></div>}
                                                                                {detail.clinicalStatus}
                                                                            </div>
                                                                            <OverflowRow text={detail.note} className={'is-ellipsys'} item={detail} getFieldHistory={getFieldHistory} TooltipContent={TooltipContent} />
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        }
                                                        <GlobalBeforeUnloadListener isChanged={group2.isEditMode || group2.isEditSendSeparatelyMode} />

                                                        {!patientName && group2.show && <div className="is-dd">
                                                            {!patientName && <div className="card--import__btns">
                                                                <ReactTooltip positionStrategy="fixed" id="reactTooltipDNS" place="top" />
                                                                {group2.hasNonScheduledItems && <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--calendar-x" onClick={() => showNonScheduledItems(index, index2)} data-tooltip-content={group2.showNonScheduledItems ? `Hide Non-scheduled items` : `View Non-scheduled items`} data-tooltip-id="reactTooltipDNS" disabled={group2.isEditMode || group2.isEditSendSeparatelyMode || disabledItemButtons} />}
                                                                {group2.hasDNSItems && <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--order-cancel" onClick={() => showDNSItems(index, index2)} data-tooltip-content={group2.showDNSItems ? `Hide DNS` : `View DNS`} data-tooltip-id="reactTooltipDNS" disabled={group2.isEditMode || group2.isEditSendSeparatelyMode || disabledItemButtons} />}
                                                                {showRoundOption && group2.showRoundOption && (isFacilityOrderSent ? isMasterEditor : ((!isOrderSummaryStarted && shouldShowItemActionButtons) || isAdmin)) && <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--order-refresh" disabled={disabledSendOrder || group2.isEditMode || group2.isEditSendSeparatelyMode || (isEditDisabled && !isMasterEditor)} onClick={async () => await openRoundItemsModal(group2)}></Button>}
                                                                {(isFacilityOrderSent ? isMasterEditor : ((shouldShowItemActionButtons && !isOrderSummaryStarted) || isAdmin)) && <Button style={{ position: 'relative' }} className={`btn no-hover ${group2.isEditSendSeparatelyMode ? "is-selected" : ""}`} icon="u-icon u-icon--med-lrg u-icon--order-ss" disabled={disabledSendOrder || group2.isEditMode || group2.isEditSendSeparatelyMode} onClick={() => { group2.isEditSendSeparatelyMode = true; group2.isEditMode = false; }}></Button>}
                                                                {(group2.isEditSendSeparatelyMode) &&
                                                                    <div className="ss-div">
                                                                        <div className="type--sml">SS QTY</div>
                                                                        <input
                                                                            className="input--ss-qty no-arrows"
                                                                            onClick={(e) => e.currentTarget.select()}
                                                                            onKeyDown={(e) => {
                                                                                handleKeyDown(e);
                                                                                if (e.key === "Enter") {
                                                                                    e.preventDefault();
                                                                                    const inputValue = getSsQtyInputValue() || "0";
                                                                                    cutbackSendSeparatelyQuantities(group2, index, index2, inputValue);
                                                                                }
                                                                            }}
                                                                            onPaste={handlePaste}
                                                                            defaultValue={group2.sendSeparatelyQuantitySum}
                                                                            ref={(el) => {
                                                                                setSsQtyInputRef(el);
                                                                                if (el && !group2.isDefaultUpdated) {
                                                                                    el.focus();
                                                                                    el.select();
                                                                                }
                                                                            }}
                                                                            onChange={(e) => editBatchSendSeparatelyQty(group2, index, index2, e.target.value)}
                                                                            type="number"
                                                                            min="0"
                                                                            disabled={disabledSendOrder}
                                                                        />
                                                                        <div className="u-flex--primary u-flex--no-wrap u-flex--gap--2">
                                                                            <Button className="btn btn--patient ex-hover" icon="u-icon u-icon--med-lrg u-icon--patient-check" disabled={disabledSendOrder} onClick={async () => { const inputValue = getSsQtyInputValue() || '0'; group2.isEditSendSeparatelyMode ? await cutbackSendSeparatelyQuantities(group2, index, index2, inputValue) : await cutbackSOQuantities("cutback-specific", group2, index, index2) }} />
                                                                            <Button className="btn btn--patient ex-hover" icon="u-icon u-icon--med-lrg u-icon--patient-close" disabled={disabledSendOrder} onClick={() => { group2.isEditMode = false; group2.isEditSendSeparatelyMode = false; refreshEditModeItems(index, index2); resetSSQuantitySum(group2); }} />
                                                                        </div>
                                                                    </div>
                                                                }
                                                                {(isFacilityOrderSent ? isMasterEditor : ((shouldShowItemActionButtons && !isOrderSummaryStarted) || isAdmin)) &&
                                                                    <CutbackOptions
                                                                        buttonClass={`btn--icon--dd ${group2.isEditMode ? "is-selected" : ""}`}
                                                                        cutbackSalesOrderQuantity={cutbackSalesOrderQuantity}
                                                                        items={facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2]}
                                                                        index={index} index2={index2}
                                                                        disabled={disabledSendOrder || group2.isEditMode || group2.isEditSendSeparatelyMode}
                                                                        isEditMode={group2.isEditMode}
                                                                    />
                                                                }
                                                                {
                                                                    (group2.isEditMode) &&
                                                                    <div className="u-flex--primary u-flex--no-wrap u-flex--gap--2 card--import__actions">
                                                                        <Button className="btn btn--patient ex-hover" icon="u-icon u-icon--med-lrg u-icon--patient-check" disabled={disabledSendOrder} onClick={async () => { group2.isEditSendSeparatelyMode ? await cutbackSendSeparatelyQuantities(group2, index, index2) : await cutbackSOQuantities("cutback-specific", group2, index, index2) }} />
                                                                        <Button className="btn btn--patient ex-hover" icon="u-icon u-icon--med-lrg u-icon--patient-close" disabled={disabledSendOrder} onClick={() => { group2.isEditMode = false; group2.isEditSendSeparatelyMode = false; refreshEditModeItems(index, index2); }} />
                                                                    </div>
                                                                }

                                                            </div>}
                                                            {<Button className="btn btn--icon card--import__btn" icon="u-icon u-icon--intermed u-icon--arrow-up" disabled={disabledSendOrder} onClick={() => showFacilityOrderTemplateItemOrderSummaryDetails(index, index2)}></Button>}
                                                        </div>}
                                                    </div>
                                                    {group2.isRefreshed && <div className="u-padd--x--med w--100">
                                                        <div className="card card--outlined--featured card--outlined--note card--xsml card--flex">
                                                            <div className="u-flex--space-between">
                                                                <div>
                                                                    <i className="u-icon u-icon--sml u-icon--warning-triangle u-mar--right--sml"></i>
                                                                    This item was refreshed while in edit mode due to changes made to the order. Edits were not saved.
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>}
                                                    {!patientName && facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].show &&
                                                        <FacilityOrderTemplateItemOrderSummaryDetails items={facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.filter(x => (facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showDNSItems || !isDns(x)) && (facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showNonScheduledItems || !isNonScheduled(x)))} group={group2} nikoFacilityOrderOrderSummaryPreviewViewStore={nikoFacilityOrderOrderSummaryPreviewViewStore} index={index} index2={index2} />
                                                    }
                                                </div>
                                            );
                                        })
                                        }
                                    </div>
                                </React.Fragment>
                            );
                        })
                        :
                        <React.Fragment></React.Fragment>
                    }
                </React.Fragment>
                }
            </div>
            <BaasicConfirmModal modalParams={addTubeConfirmModal} />
            <BaasicConfirmModal modalParams={cutbackConfirmModal} />
            <BaasicConfirmModal modalParams={sendOrderConfirmModal} />
            <BaasicConfirmModal modalParams={addIvPolesConfirmModal} />
            <BaasicModal modalParams={primaryRecipientErrorModal} className="modal__content modal--sml">
                <PrimaryRecipientErrorModal />
            </BaasicModal>
            <BaasicModal modalParams={facilityOrderNewFlowUSIntakeFindAndReplaceModal} className="modal__content modal--xlrg">
                <NikoFacilityOrderUSIntakeFindAndReplaceModal />
            </BaasicModal>
            <BaasicModal modalParams={facilityOrderNewFlowExtraSuppliesAddModal} className="modal__content modal--xxlrg">
                <NikoFacilityOrderNewFlowExtraSuppliesAddModal />
            </BaasicModal>
            <BaasicModal
                modalParams={facilityOrderNewFlowRoundingItemsModal}
                className={
                    facilityOrderNewFlowRoundingItemsModal &&
                        facilityOrderNewFlowRoundingItemsModal.data &&
                        facilityOrderNewFlowRoundingItemsModal.data.items
                        ? "modal__content modal--lrg"
                        : "modal__content modal--med"
                }
            >
                <FacilityOrderNewFlowRoundingItemsModal />
            </BaasicModal>
            <ScrollToTopButton />
        </PreviewLayout>
    );
};

const FacilityOrderTemplateItemOrderSummaryDetails = observer(({ items, group, nikoFacilityOrderOrderSummaryPreviewViewStore, index, index2 }) => {
    const {
        editOrderQuantity,
        saveOrderQuantity,
        facilityOrderTemplateItemsGroupedByItemCategoryEditMode,
        facilityOrderTemplateItemsGroupedByItemCategory,
        disabledSendOrder,
        isDns,
        isNonScheduled,
        isNewPatientById,
        getFieldHistory,
        getItemAddedByHistory,
    } = nikoFacilityOrderOrderSummaryPreviewViewStore;

    const TooltipContent = observer(({ initialValue, history, fieldName }) => {
        if (!history || history.fieldName !== fieldName) return null;

        const date = new Date(history.eventDate + "Z");
        const formattedDate = date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(',', '');

        if (fieldName === 'ImportAction') {
            return (<div>
                <div>Added by {history.userName}</div>
                <div style={{ fontSize: '11px', opacity: 0.75 }}>{formattedDate}</div>
            </div>);
        }

        return (<div>
            <div>Previous: {initialValue ? initialValue : 'blank'}</div>
            <div style={{ fontSize: '11px', opacity: 0.75 }}>
                {history ? history.userName + ', ' + formattedDate : '—'}
            </div>
        </div>)
    });

    const isEditSSMode = (
        facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.some(x => x.sendSeparatelyQty && x.sendSeparatelyQty != 0) ||
        facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.some(x => x.sendSeparatelyQty && x.sendSeparatelyQty != 0));

    let isFormulaColumn = false;

    if (
        group &&
        group.facilityOrderTemplateItemOrderSummaryDetails &&
        group.facilityOrderTemplateItemOrderSummaryDetails.some(item =>
            !item.isMasterPatient
        ) &&
        (group.itemTypeId || (group.procedureCode && group.procedureCode === "B4155"))
    ) {
        isFormulaColumn = true;
    }

    return (

        <div className="table--preview__wrapper is-order u-mar--bottom--tny">
            <table className={`table--preview w--100 ${isEditSSMode ? 'has-cc' : ''}`}>
                <colgroup>
                    <col className="order__col--1" />
                    <col className="order__col--2" />
                    <col className="order__col--3" />
                    {isEditSSMode &&
                        <col className="order__col--4" />}
                    <col className="order__col--5" />
                    <col className="order__col--6" />
                </colgroup>
                <thead>
                    <tr>
                        <th>PATIENT NAME</th>
                        <th>TEMP QTY</th>
                        <th>SO QTY</th>
                        {isEditSSMode && <th>SS QTY</th>}
                        <th>ELIGIBILITY STATUS</th>
                        <th>CLINICAL STATUS</th>
                        <th>{isFormulaColumn ? "Cal Amount" : "Item note"}</th>
                    </tr>
                </thead>
                <tbody>
                    {items && items.map((item) => {
                        return (
                            <tr key={item.facilityOrderTemplateItemId} className={`${(isDns(item) || isNonScheduled(item)) ? 'is-not-supply' : ''} ${item.isNewTemplateItem ? 'new-item' : ''}`}>
                                <td style={{ position: 'relative' }}>
                                    {item.isNewTemplateItem && <React.Fragment>
                                        <ReactTooltip positionStrategy="fixed" id={"patientItemAddedBy" + item.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={null} history={item.history} fieldName={"ImportAction"} />} />
                                        <div className="table-col-input__notif table-col-input__notif--insert" data-tooltip-id={"patientItemAddedBy" + item.facilityOrderTemplateItemId} onMouseEnter={async () => await getItemAddedByHistory(item)} data-tooltip-content="" />
                                    </React.Fragment>}
                                    <div className="u-flex--default u-flex--space-between">
                                        {item.patient}
                                        <div>
                                            <i className={`${item.isMasterPatient ? 'u-icon u-icon--med u-icon--new-facility--primary' : ''}`} ></i>
                                        </div>
                                    </div>
                                </td>
                                <td className="table-col-input">
                                    <ReactTooltip positionStrategy="fixed" id={"templateQuantityChanged" + item.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={item.initialTemplateQuantity} history={item.history} fieldName={"Quantity"} />} />
                                    {(!item.isNewTemplateItem && !!item.initialTemplateQuantity && item.templateQuantity != item.initialTemplateQuantity) && <div className="table-col-input__notif opacity-50" data-tooltip-id={"templateQuantityChanged" + item.facilityOrderTemplateItemId} onMouseEnter={async () => await getFieldHistory(item, 'Quantity', item.templateQuantity)} data-tooltip-content="" ></div>}
                                    {item.templateQuantity}
                                </td>
                                {group.isEditMode && (!isDns(item) && !isNonScheduled(item)) ?
                                    <td className="table-col-input">
                                        <input
                                            className="input--table input--inherit-color no-arrows input--text-align--left w--70--px"
                                            onClick={(e) => e.currentTarget.select()}
                                            onKeyDown={handleKeyDown}
                                            onPaste={handlePaste}
                                            onChange={(e) => { editOrderQuantity(item, index, index2, e.target.value) }}
                                            onBlur={(e) => { saveOrderQuantity(item, index, index2, e.target.value) }}
                                            value={facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId == item.facilityOrderTemplateItemId).orderQuantity}
                                            type="number"
                                            disabled={disabledSendOrder}
                                        />
                                    </td> :
                                    <td className="table-col-input is-flex">
                                        <ReactTooltip positionStrategy="fixed" id={"orderQuantityChanged" + item.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={item.initialOrderQuantity} history={item.history} fieldName={"SalesOrderQuantity"} />} />
                                        {(!item.isNewTemplateItem && !!item.initialOrderQuantity && item.orderQuantity != item.initialOrderQuantity) && <div className="table-col-input__notif opacity-50" data-tooltip-id={"orderQuantityChanged" + item.facilityOrderTemplateItemId} onMouseEnter={async () => await getFieldHistory(item, 'SalesOrderQuantity', item.orderQuantity)} data-tooltip-content="" ></div>}
                                        {item.orderQuantity}
                                        {(item.isEligibleToSend !== null && item.activeStatus === "Active" && !isDns(item) && item.orderQuantity > 0) &&
                                            (!item.isEligibleToSend ?
                                                <i className="u-icon u-icon--med u-icon--calendar-x--dark u-align--v--text-bottom u-mar--left--tny" ></i> :
                                                <i className="u-icon u-icon--med u u-icon--calendar-yes--dark u-align--v--text-bottom u-mar--left--tny" ></i>
                                            )
                                        }
                                        {isDns(item) && <i className="u-icon u-icon--med u-icon--order-cancel--grey" ></i>}
                                    </td>}
                                {(facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.some(x => x.sendSeparatelyQty && x.sendSeparatelyQty != 0) ||
                                    facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.some(x => x.sendSeparatelyQty && x.sendSeparatelyQty != 0)) &&
                                    <td className="table-col-input">
                                        {item.sendSeparatelyQty}
                                    </td>}
                                <td className="table-col-input">
                                    <ReactTooltip positionStrategy="fixed" id={"eligibilityStatusChanged" + item.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={item.initialEligibilityStatus ? item.initialEligibilityStatus : 'Blank'} history={item.history} fieldName={"EligibilityStatusId"} />} />
                                    {(!isNewPatientById(item.patientId) && item.eligibilityStatus != item.initialEligibilityStatus) && <div className="table-col-input__notif opacity-50" data-tooltip-id={"eligibilityStatusChanged" + item.facilityOrderTemplateItemId} onMouseEnter={async () => await getFieldHistory(item, 'EligibilityStatusId', item.eligibilityStatusId, item.facilityOrderTemplateId)} data-tooltip-content="" ></div>}
                                    {item.eligibilityStatus}
                                </td>
                                <td className="table-col-input">
                                    <ReactTooltip positionStrategy="fixed" id={"clinicalStatusChanged" + item.facilityOrderTemplateItemId} place="top" delayShow={650} render={() => <TooltipContent initialValue={item.initialClinicalStatus ? item.initialClinicalStatus : 'Blank'} history={item.history} fieldName={"ClinicalStatusId"} />} />
                                    {(!item.isNewTemplate && item.clinicalStatus != item.initialClinicalStatus) && <div className="table-col-input__notif opacity-50" data-tooltip-id={"clinicalStatusChanged" + item.facilityOrderTemplateItemId} onMouseEnter={async () => await getFieldHistory(item, 'ClinicalStatusId', item.clinicalStatusId, item.facilityOrderTemplateId)} data-tooltip-content="" ></div>}
                                    {item.clinicalStatus}
                                </td>
                                <NoteCell item={item} getFieldHistory={getFieldHistory} TooltipContent={TooltipContent} />
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>

    );
});

FacilityOrderTemplateItemOrderSummaryDetails.propTypes = {
    items: PropTypes.any,
};

NikoFacilityOrderOrderSummaryPreviewTemplate.propTypes = {
    nikoFacilityOrderOrderSummaryPreviewViewStore: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    t: PropTypes.func
};

// Develop wraps SearchInput / CutbackOptions / BackorderNoteControl
// with `@observer`; the mobx-undecorate codemod stripped all three
// decorators without converting to the new `observer(Component)`
// call form, so each component stopped subscribing to the observables
// it reads (e.g. `facilityOrderTemplateItemsGroupedByItemCategory`
// items, group-level flags). Restore the wrap.
const SearchInput = observer(class SearchInput extends React.Component {
    constructor(props) {
        super(props);
        this.autoCompleteRef = React.createRef();
        // True when the most recent mousedown inside this widget landed on the
        // clear-X. Kendo v14 natively focuses the input right after a clear-X click,
        // which would reopen the list; handleFocus reads this to keep it closed, while
        // a genuine click on the (empty or filled) field still opens it — matching
        // develop. Tracked by a passive mousedown listener (no stopPropagation, so
        // Kendo's delegated clear-onClick still runs).
        this.mouseDownOnClear = false;
    }

    state = {
        data: this.props.source,
        value: this.props.value || '',
        focus: false
    };

    componentDidUpdate(prevProps) {
        if (prevProps.source !== this.props.source) {
            this.setState({ data: this.props.source });
        }
        if (prevProps.value !== this.props.value) {
            this.setState({ value: this.props.value || '' });
        }
    }

    componentDidMount() {
        const el = this.autoCompleteRef.current && this.autoCompleteRef.current.element;
        if (el) {
            el.addEventListener('mousedown', this.handleHostMouseDown, true);
            el.addEventListener('paste', this.handlePasteCapture, true);
        }
    }

    componentWillUnmount() {
        const el = this.autoCompleteRef.current && this.autoCompleteRef.current.element;
        if (el) {
            el.removeEventListener('mousedown', this.handleHostMouseDown, true);
            el.removeEventListener('paste', this.handlePasteCapture, true);
        }
    }

    // mousedown fires before the focus it triggers, so mouseDownOnClear is set in
    // time for handleFocus to read it. The popup list is portaled outside this
    // element, so selecting a suggestion never trips this.
    handleHostMouseDown = (e) => {
        const onClear = !!(e.target.closest && e.target.closest('.k-clear-value'));
        this.mouseDownOnClear = onClear;
        // A press on the field itself opens the list — even when the input is already
        // focused, which is the case right after a clear-X (Kendo v14 leaves the field
        // focused, so clicking it fires no new focus event and handleFocus wouldn't
        // run). A press on the clear-X must NOT open it.
        if (!onClear) {
            this.setState({ open: true });
        }
    };

    blurInput = () => {
        if (this.autoCompleteRef.current && this.autoCompleteRef.current.element) {
            const input = this.autoCompleteRef.current.element.querySelector('input');
            if (input) input.blur();
        }
    };

    handleFocus = () => {
        // A genuine click on the field (empty or filled) opens the list — matching
        // develop. But Kendo v14 fires onFocus right after a clear-X click (the X
        // natively focuses the input), which must NOT reopen the list. The mousedown
        // that triggered this focus tells them apart: if it landed on the clear-X,
        // stay closed. (mouseDownOnClear is reset on blur so a later keyboard/tab
        // focus still opens.)
        this.setState({ open: !this.mouseDownOnClear, focus: true });
    };

    handleBlur = () => {
        this.mouseDownOnClear = false;
        this.setState({ open: false, focus: false });
    };

    onChange = (event) => {
        this.applyValue(event.target.value);
    };

    // Shared by typing (onChange) and pasting (handlePasteCapture).
    applyValue = (value) => {
        const isClear = value === "";
        const isCompleteMatch = this.props.source.some(item => (this.props.textField ? item[this.props.textField] : item) === value);
        // We do NOT use onClose — Kendo v14 fires it spuriously when the focus-opened
        // popup is clicked while the field already holds a matching value, which made
        // the list flicker (v3 did not). Keep the list open while typing a partial
        // search; close on a complete match (selection) or when cleared.
        this.setState({
            data: this.filterData(value),
            value: value,
            open: !isClear && !isCompleteMatch
        });
        if (isCompleteMatch) {
            this.blurInput();
        }
        this.props.onChange(value);
    };

    // Kendo's AutoComplete input is inline (not portaled), so a native paste
    // listener on the host element catches the paste before the browser inserts
    // the raw text. Trim the pasted fragment, splice it in at the caret, then
    // route the result through the same path as typing.
    handlePasteCapture = (e) => {
        const result = getTrimmedPasteResult(e);
        if (result) {
            this.applyValue(result.nextValue);
        }
    };

    filterData(value) {
        const data = this.props.source;
        var filter = this.props.filterBy.map(x => { return { field: x.field, operator: x.operator, value: value, ignoreCase: true } });
        return value ? filterBy(data, {
            logic: 'or', filters: filter
        }) : data;
    }

    itemRender(li, itemProps) {
        const itemChildren = (
            <span className="k-item__content">
                {itemProps && itemProps.dataItem.icon &&
                    <i className="u-icon u-icon--sml u-icon--user u-mar--right--xsml"></i>
                }
                <span className={` ${itemProps.dataItem.description ? 'is-nowrap' : ''} k-autocomplete__name`}>
                    {li.props.children}
                </span>

                {itemProps && itemProps.dataItem.tag &&
                    <span className="k-autocomplete__tag is-warning">
                        {itemProps.dataItem.tag}
                    </span>
                }

                {itemProps && itemProps.dataItem.description &&
                    <span className="u-mar--left--xsml">
                        {itemProps.dataItem.description}
                    </span>
                }
            </span>
        );
        return React.cloneElement(li, (itemProps && itemProps.dataItem.disabled) ? { ...li.props, onClick: undefined } : li.props, itemChildren);
    }

    render() {
        return (
            <AutoComplete
                className={"search-input has-icon"}
                ref={this.autoCompleteRef}
                textField={this.props.textField}
                data={this.state.data}
                itemRender={this.itemRender}
                value={this.state.value}
                onChange={this.onChange}
                placeholder={this.props.placeholder}
                label=""
                opened={this.state.open}
                disabled={this.props.disabled}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
            />
        );
    }
});

SearchInput.propTypes = {
    source: PropTypes.object,
    disabled: PropTypes.bool,
    textField: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    placeholder: PropTypes.string,
    filterBy: PropTypes.array,
    value: PropTypes.string
};

const CutbackOptions = observer(class CutbackOptions extends React.Component {
    constructor(props) {
        super(props);
    }

    render() {
        return (
            <HoverDropDownButton
                enableClickToggle={true}
                iconClass="u-icon u-icon--intermed u-icon--order-box"
                buttonClass={this.props.buttonClass}
                popupSettings={{
                    animate: false,
                    anchorAlign: { horizontal: "right", vertical: "bottom" },
                    popupAlign: { horizontal: "right", vertical: "top" },
                    popupClass: 'is-clean'
                }}
                disabled={this.props.disabled}
                onItemClick={async (e) => {
                    const { text } = e.item;
                    if (text === "Cutback on All Noncovered") {
                        await this.props.cutbackSalesOrderQuantity("cutback-noncovered", this.props.items, this.props.index, this.props.index2);
                    } else if (text === "Cutback on All") {
                        await this.props.cutbackSalesOrderQuantity("cutback-all", this.props.items, this.props.index, this.props.index2);
                    } else if (text === "Cutback on Specific Pts") {
                        await this.props.cutbackSalesOrderQuantity("cutback-specific", this.props.items, this.props.index, this.props.index2);
                    }
                }}
            >
                <DropDownButtonItem text="Cutback on All Noncovered" />
                <DropDownButtonItem text="Cutback on All" />
                <DropDownButtonItem text="Cutback on Specific Pts" />
            </HoverDropDownButton>
        )
    }
});

CutbackOptions.propTypes = {
    items: PropTypes.array,
    cutbackSalesOrderQuantity: PropTypes.func,
    disabled: PropTypes.bool,
    index: PropTypes.any,
    index2: PropTypes.any,
    isEditMode: PropTypes.bool,
    buttonClass: PropTypes.string,
};

class ComboBoxWrapper extends React.Component {
    constructor(props) {
        super(props);
        this.comboBoxRef = React.createRef();

        this.state = {
            opened: false,
            filteredData: props.data || [],
            focus: false,
        };
    }

    blurInput = () => {
        if (this.comboBoxRef.current && this.comboBoxRef.current.element) {
            const input = this.comboBoxRef.current.element.querySelector('input');
            if (input) input.blur();
        }
    };

    componentDidUpdate(prevProps) {
        if (prevProps.data !== this.props.data) {
            this.setState({ filteredData: this.props.data || [] });
        }
    }

    componentDidMount() {
        const el = this.comboBoxRef.current && this.comboBoxRef.current.element;
        if (el) el.addEventListener('paste', this.handlePasteCapture, true);
    }

    componentWillUnmount() {
        const el = this.comboBoxRef.current && this.comboBoxRef.current.element;
        if (el) el.removeEventListener('paste', this.handlePasteCapture, true);
    }

    // ComboBox owns its editable text internally (unlike SearchInput, whose text
    // is controlled via state), so intercept the paste, trim the pasted fragment,
    // splice it at the caret, then push the result back through the native input
    // setter + input event — Kendo reflects the trimmed text and fires
    // onFilterChange as if it were typed, leaving the rest of the flow unchanged.
    handlePasteCapture = (e) => {
        const input = e.target;
        const result = getTrimmedPasteResult(e);
        if (!result) {
            return;
        }

        const valueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
        valueSetter.call(input, result.nextValue);
        input.dispatchEvent(new Event('input', { bubbles: true }));

        input.setSelectionRange(result.caret, result.caret);
    };

    handleFocus = () => {
        this.setState({ opened: true, focus: true });
    };

    handleBlur = () => {
        this.setState({ opened: false, focus: false });
    };

    handleChange = (event) => {
        const value = event.value;

        const isClear = value === null;
        const inputIsFocused = this.state.focus;
        this.setState({ opened: !isClear ? true : inputIsFocused });

        const inputText =
            typeof value === "string"
                ? value.toLowerCase()
                : value && value[this.props.textField]
                    ? value[this.props.textField].toLowerCase()
                    : "";

        if (!inputText.trim()) {
            if (this.props.onChange) {
                this.props.onChange(null);
            }
            return;
        }

        const matches = this.state.filteredData.filter((item) => {
            return (
                item &&
                item[this.props.textField] &&
                item[this.props.textField].toLowerCase() === inputText
            );
        });

        let selected;

        if (matches.length > 0) {
            selected = matches[0];
            this.blurInput()
        } else if (inputText) {
            selected = {
                itemName: inputText,
                itemId: new Date().getTime(),
                description: inputText,
            };
            this.blurInput()
        }
        if (this.props.onChange) {
            this.props.onChange(selected);
        }

        this.setState({ opened: false });
    };

    handleFilterChange = (event) => {
        const filterValue = event.filter.value || "";
        const filterValueLower = filterValue.toLowerCase();

        const filteredData = (this.props.data || []).filter((item) => {
            const name = item && item.itemName ? item.itemName.toLowerCase() : "";
            const id = item && item.itemId ? item.itemId.toLowerCase() : "";
            return name.includes(filterValueLower) || id.includes(filterValueLower);
        });

        this.setState({ filteredData });
    };

    itemRender = (li, itemProps) => {
        const item = itemProps.dataItem;
        return (
            <li {...li.props}>
                {item.itemId} {item.itemName}
            </li>
        );
    };

    render() {
        return (
            <ComboBox
                data={this.state.filteredData || this.props.data}
                value={this.props.value}
                onChange={this.handleChange}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                opened={this.state.opened}
                filterable={true}
                textField={this.props.textField}
                allowCustom={this.props.allowCustom}
                dataItemKey={this.props.dataItemKey}
                disabled={this.props.disabled}
                placeholder="Search Item"
                className="combobox--search"
                onFilterChange={this.handleFilterChange}
                focusedItemIndex={() => (this.state.filteredData && this.state.filteredData.length ? 0 : -1)}
                ref={this.comboBoxRef}
                itemRender={this.itemRender}
            />
        );
    }
}

ComboBoxWrapper.propTypes = {
    data: PropTypes.array.isRequired,
    textField: PropTypes.string.isRequired,
    dataItemKey: PropTypes.string,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    value: PropTypes.any,
    allowCustom: PropTypes.bool,
};

class NoteCell extends React.Component {
    constructor(props) {
        super(props);
        this.noteRef = React.createRef();
        this.state = {
            isOverflowing: false,
        };
    }

    componentDidMount() {
        setTimeout(() => this.checkOverflow(), 0);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.item.note !== this.props.item.note) {
            this.checkOverflow();
        }
    }

    checkOverflow() {
        const el = this.noteRef.current;
        if (el && this.props.item.note) {
            const isOverflowing =
                el.scrollWidth > el.clientWidth ||
                el.scrollHeight > el.clientHeight;
            this.setState({ isOverflowing });
        } else {
            this.setState({ isOverflowing: false });
        }
    }

    render() {
        const { item, getFieldHistory, TooltipContent } = this.props;
        const { isOverflowing } = this.state;

        return (
            <td
                className={`table-col-input has-note ${isOverflowing ? "has-note--overflow" : ""
                    }`}

            >
                <ReactTooltip positionStrategy="fixed"
                    id={"noteChanged_table_" + item.facilityOrderTemplateItemId}
                    place="top"
                    delayShow={650}
                    render={() => <TooltipContent initialValue={item.initialNote ? item.initialNote : 'blank'} history={item.history} fieldName={"Note"} />}
                />

                {!item.isNewTemplateItem && item.isNoteChanged && (
                    <div
                        className="table-col-input__notif opacity-50"
                        data-tooltip-id={"noteChanged_table_" + item.facilityOrderTemplateItemId}
                        onMouseEnter={async () => await getFieldHistory(item, 'Note', item.note)}
                        data-tooltip-content=""
                    ></div>
                )}

                <span ref={this.noteRef} className="note--sml">
                    {item.note}
                </span>

                {isOverflowing && <div className="note--long">{item.note}</div>}
            </td>
        );
    }
}
NoteCell.propTypes = {
    item: PropTypes.any,
    getFieldHistory: PropTypes.func,
    TooltipContent: PropTypes.any,
};

class OverflowRow extends React.Component {
    constructor(props) {
        super(props);
        this.textRef = React.createRef();
        this.state = {
            isOverflowing: false,
        };
    }

    componentDidMount() {
        this.checkOverflow()
    }

    componentDidUpdate(prevProps) {
        if (prevProps.text !== this.props.text) {
            this.checkOverflow();
        }
    }

    checkOverflow() {
        const el = this.textRef.current;

        if (el) {
            const isOverflowing =
                el.scrollWidth > el.clientWidth ||
                el.scrollHeight > el.clientHeight;
            this.setState({ isOverflowing });
        }
    }

    render() {
        const { text, className, item, getFieldHistory, TooltipContent } = this.props;
        const { isOverflowing } = this.state;

        return (
            <div className={`is-row ${className}`}>

                {item && getFieldHistory && TooltipContent &&
                    <React.Fragment>
                        <ReactTooltip positionStrategy="fixed"
                            id={"noteChanged_card_" + item.facilityOrderTemplateItemId}
                            place="top"
                            delayShow={650}
                            render={() => <TooltipContent initialValue={item.initialNote ? item.initialNote : 'blank'} history={item.history} fieldName={"Note"} />}
                        />

                        {!item.isNewTemplateItem && item.isNoteChanged && (
                            <div
                                className="table-col-input__notif opacity-50"
                                data-tooltip-id={"noteChanged_card_" + item.facilityOrderTemplateItemId}
                                onMouseEnter={async () => await getFieldHistory(item, 'Note', item.note)}
                                data-tooltip-content=""
                            ></div>
                        )}
                    </React.Fragment>
                }
                {item && !getFieldHistory &&
                    <React.Fragment>
                        <ReactTooltip positionStrategy="fixed"
                            id={"noteChanged_card_" + item.id}
                            place="top"
                        />

                        {!item.isNewTemplateItem && item.isNoteChanged && (
                            <div
                                className="table-col-input__notif opacity-50"
                                data-tooltip-id={"noteChanged_card_" + item.id}
                                data-tooltip-content={
                                    "Previous: " +
                                    (item && item.initialNote
                                        ? item.initialNote
                                        : "blank")
                                }
                            ></div>
                        )}
                    </React.Fragment>
                }

                <span className="short-text" ref={this.textRef}>
                    {text}
                </span>
                {isOverflowing && (
                    <div className="long-text">
                        {text}
                    </div>
                )}
            </div>
        );
    }
}

OverflowRow.propTypes = {
    text: PropTypes.any,
    className: PropTypes.string,
    item: PropTypes.any,
    getFieldHistory: PropTypes.func,
    TooltipContent: PropTypes.any,
};

// Develop wraps this class with `@observer` (the legacy decorator
// syntax) so it re-renders when the `group.backorderNote` observable
// mutates after the `updateBackorderNote` await resolves. The
// mobx-undecorate codemod stripped the decorator across the codebase
// (the project now uses `observer(Component)` call form everywhere),
// but this class lost its `observer` wrap entirely — so on save the
// store mutates `group.backorderNote`, the parent template re-renders
// with the same `group` prop reference, React sees no prop change
// and skips reconciling this child, and the icon stays stuck in its
// pre-save state. Restore the observer wrap.
const BackorderNoteControl = observer(class BackorderNoteControl extends React.Component {
    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this.state = { isEditing: false };
    }

    handleIconClick = (e) => {
        e.stopPropagation();
        this.setState({ isEditing: true }, () => {
            if (this.inputRef.current) {
                this.inputRef.current.focus();
            }
        });
    };

    handleBlur = (e) => {
        const value = e.target.value.trim();
        const { group, updateBackorderNote } = this.props;
        if (value !== (group.backorderNote || '')) {
            updateBackorderNote(group, value);
        }
        this.setState({ isEditing: false });
    };

    render() {
        const { group, isPurchaser } = this.props;
        const { isEditing } = this.state;
        const hasNote = group.backorderNote && group.backorderNote !== '';

        if (!isPurchaser) {
            if (!hasNote) return null;
            return (
                <div className="u-flex--default u-flex--gap--2">
                    <i className="u-icon u-icon--med-lrg u-icon--clock--red" />
                    <span className="type--break-all">{group.backorderNote}</span>
                </div>
            );
        }

        // State 1+2: no value — icon visible only on item line hover
        if (!isEditing && !hasNote) {
            return (
                <div className="bo-note--on-hover u-mar--left--auto">
                    <ReactTooltip positionStrategy="fixed" id={"boNoteTip_" + group.productId} place="top" />
                    <div className="u-icon--has-bg" onClick={this.handleIconClick} data-tooltip-id={"boNoteTip_" + group.productId} data-tooltip-content="Backorder Note">
                        <i className="u-icon u-icon--med-lrg u-icon--clock--primary u-cursor--pointer" />
                    </div>
                </div>
            );
        }

        // State 3: editing — | [INPUT] [red icon + red bg]
        if (isEditing) {
            return (
                <div className="u-flex--default input--btn u-mar--left--auto">
                    <input
                        ref={this.inputRef}
                        className="input input--cell"
                        defaultValue={group.backorderNote || ''}
                        key={group.productId + '_bo_input'}
                        onBlur={this.handleBlur}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                    />
                    <div className="u-icon--has-bg">
                        <i className="u-icon u-icon--med-lrg u-icon--clock--red" />
                    </div>
                </div>
            );
        }

        // State 4: has value — | [red icon] {text}, click on icon or text opens edit
        return (
            <div className="u-flex--default u-flex--gap--2" onClick={this.handleIconClick}>
                <i className="u-icon u-icon--med-lrg u-icon--clock--red u-flex--shrink--0" />
                <span className="type--break-all">{group.backorderNote}</span>
            </div>
        );
    }
});

BackorderNoteControl.propTypes = {
    group: PropTypes.object.isRequired,
    updateBackorderNote: PropTypes.func.isRequired,
    isPurchaser: PropTypes.bool,
};

export default defaultTemplate(NikoFacilityOrderOrderSummaryPreviewTemplate);
